import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { FaGamepad, FaGlobe, FaSteam, FaSync, FaBook, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import useDirectusData from '../../hooks/useDirectusData';
import { fetchServices, getImageUrl } from '../../api/directus';

/**
 * ServicesSection component for the homepage
 * Displays the services offered by NatuXtranslations
 * Fetches data from Directus CMS
 * Designed with an elegant, minimalistic black and white approach
 * Supports multiple languages through i18n
 */
const ServicesSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  
  // Fetch services data from Directus CMS
  const { data, loading, error } = useDirectusData(fetchServices);
  
  // Map icon names to React icons
  const getIconComponent = (iconString) => {
    // If the icon is an SVG string from the CMS, return it as dangerouslySetInnerHTML with size constraints
    if (iconString && iconString.startsWith('<svg')) {
      return (
        <div 
          className="w-16 h-16 flex items-center justify-center overflow-hidden" 
          dangerouslySetInnerHTML={{ 
            __html: iconString.replace('<svg', '<svg class="w-full h-full"') 
          }} 
        />
      );
    }
    
    // Otherwise use a fallback icon based on the service ID or title
    switch (iconString) {
      case 'a': // For community management service
        return <FaUsers className="text-5xl" />;
      case 'b':
        return <FaBook className="text-5xl" />;
      default:
        // Default icons based on service ID
        const icons = [
          <FaGamepad className="text-5xl" />,
          <FaGlobe className="text-5xl" />,
          <FaSteam className="text-5xl" />,
          <FaSync className="text-5xl" />,
          <FaBook className="text-5xl" />,
          <FaUsers className="text-5xl" />
        ];
        return icons[Math.min(5, Math.max(0, parseInt(iconString) - 1))] || <FaGamepad className="text-5xl" />;
    }
  };
  
  // Create a services array from the CMS data or use fallback if loading/error
  const services = data?.data ? 
    data.data.slice(0, 4).map(service => ({
      icon: getIconComponent(service.icon),
      title: service.title,
      description: service.description,
      link: `/services#${service.title.toLowerCase().replace(/\s+/g, '-')}`
    })) : 
    [
      {
        icon: <FaGamepad className="text-5xl" />,
        title: t('services.service1.title'),
        description: t('services.service1.description'),
        link: "/services#game-translation"
      },
      {
        icon: <FaGlobe className="text-5xl" />,
        title: t('services.service2.title'),
        description: t('services.service2.description'),
        link: "/services#website-localization"
      },
      {
        icon: <FaSteam className="text-5xl" />,
        title: t('services.service3.title'),
        description: t('services.service3.description'),
        link: "/services#steam-page"
      },
      {
        icon: <FaSync className="text-5xl" />,
        title: t('services.service4.title'),
        description: t('services.service4.description'),
        link: "/services#ongoing-support"
      }
    ];

  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  return (
    <section ref={sectionRef} className="py-32 bg-white scroll-section relative">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/4 h-px bg-primary/10"></div>
      
      <div className="container mx-auto px-6">
        <motion.div 
          className="max-w-3xl mx-auto mb-24 text-center"
          variants={titleVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
          <h2 className="font-heading text-3xl md:text-5xl font-normal mb-8 tracking-wide">
            {t('services.title')}
          </h2>
          <p className="text-light text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
            {t('services.description')}
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {services.map((service, index) => (
            <motion.div 
              key={index} 
              className="border border-primary/10 p-10 hover:border-primary transition-all duration-500 group reveal"
              variants={itemVariants}
            >
              <div className="mb-8 text-primary opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                {service.icon}
              </div>
              <h3 className="text-2xl font-heading mb-6 tracking-wide">{service.title}</h3>
              <p className="text-light font-light text-lg mb-8">{service.description}</p>
              <Link 
                to={service.link} 
                className="inline-flex items-center text-primary font-light tracking-wider hover:text-secondary transition-colors duration-300"
              >
                {t('services.learnMore')}
                <svg className="w-4 h-4 ml-3 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </Link>
            </motion.div>
          ))}
        </motion.div>
        
        <div className="text-center mt-20">
          <Link 
            to="/services" 
            className="inline-block bg-primary text-white px-10 py-4 uppercase tracking-widest text-sm font-light transition-all duration-300 hover:bg-white hover:text-primary hover:border-primary border border-transparent"
          >
            {t('navigation.viewAllServices')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
